import axiosInstance from "../../../../common/axiosInstance";

export const EmployeeService = {
  // ==========================================
  // 1. EMPLOYEES (Read, Create & Update)
  // ==========================================

  // GET All Employees (Paginated & Filtered)
  getAllEmployees: async (page = 1) => {
    try {
      const response = await axiosInstance.get(`/employees?page=${page}`);
      return {
        data: response.data.data || [],
        meta: response.data.meta || { current_page: 1, last_page: 1, total: 0 },
      };
    } catch (error) {
      console.error("Error fetching employees from backend:", error);
      return {
        data: [],
        meta: { current_page: 1, last_page: 1, total: 0 },
      };
    }
  },

  // POST Create New Employee (Saves directly to backend database)
  createEmployee: async (employeeData) => {
    try {
      const response = await axiosInstance.post("/employees", employeeData);
      return response.data;
    } catch (error) {
      console.error("Error creating employee in database:", error);
      throw error;
    }
  },

  // GET Employee by UUID
  getEmployeeByUuid: async (employeeUuid) => {
    try {
      const response = await axiosInstance.get(`/employees/${employeeUuid}`);
      return response.data.data;
    } catch (error) {
      console.error(`Error fetching employee ${employeeUuid}:`, error);
      return null;
    }
  },

  // PUT Update Employee
  updateEmployee: async (employeeUuid, updatedData) => {
    try {
      const response = await axiosInstance.put(
        `/employees/${employeeUuid}`,
        updatedData,
      );
      return response.data;
    } catch (error) {
      console.error(`Error updating employee ${employeeUuid}:`, error);
      throw error;
    }
  },

  // DELETE Employee by UUID
  deleteEmployee: async (employeeUuid) => {
    try {
      const response = await axiosInstance.delete(`/employees/${employeeUuid}`);
      return response.data;
    } catch (error) {
      console.error(`Error deleting employee ${employeeUuid}:`, error);
      throw error;
    }
  },

  // ==========================================
  // 2. EMPLOYEE CONTACTS (Read & Update Only)
  // ==========================================

  getContacts: async (employeeUuid) => {
    try {
      const response = await axiosInstance.get(
        `/employees/${employeeUuid}/contacts`,
      );
      return response.data.data || [];
    } catch (error) {
      console.error("Error fetching contacts:", error);
      return [];
    }
  },

  updateContact: async (employeeUuid, contactUuid, contactData) => {
    try {
      const response = await axiosInstance.put(
        `/employees/${employeeUuid}/contacts/${contactUuid}`,
        contactData,
      );
      return response.data;
    } catch (error) {
      console.error(`Error updating contact ${contactUuid}:`, error);
      throw error;
    }
  },

  // ==========================================
  // 3. EMPLOYEE ADDRESSES (Read & Update Only)
  // ==========================================

  getAddresses: async (employeeUuid) => {
    try {
      const response = await axiosInstance.get(
        `/employees/${employeeUuid}/addresses`,
      );
      return response.data.data || [];
    } catch (error) {
      console.error("Error fetching addresses:", error);
      return [];
    }
  },

  updateAddress: async (employeeUuid, addressUuid, addressData) => {
    try {
      const response = await axiosInstance.put(
        `/employees/${employeeUuid}/addresses/${addressUuid}`,
        addressData,
      );
      return response.data;
    } catch (error) {
      console.error(`Error updating address ${addressUuid}:`, error);
      throw error;
    }
  },

  // ==========================================
  // 4. EMPLOYEE DOCUMENTS (Read & Update Only)
  // ==========================================

  getDocuments: async (employeeUuid) => {
    try {
      const response = await axiosInstance.get(
        `/employees/${employeeUuid}/documents`,
      );
      return response.data.data || [];
    } catch (error) {
      console.error("Error fetching documents:", error);
      return [];
    }
  },

  updateDocument: async (employeeUuid, documentUuid, documentData) => {
    try {
      const response = await axiosInstance.put(
        `/employees/${employeeUuid}/documents/${documentUuid}`,
        documentData,
      );
      return response.data;
    } catch (error) {
      console.error(`Error updating document ${documentUuid}:`, error);
      throw error;
    }
  },

  // ==========================================
  // 5. ORGANISATION ASSIGNMENTS (Read & Update Only)
  // ==========================================

  getOrgAssignments: async (employeeUuid) => {
    try {
      const response = await axiosInstance.get(
        `/employees/${employeeUuid}/organisation-assignments`,
      );
      return response.data.data || [];
    } catch (error) {
      console.error("Error fetching org assignments:", error);
      return [];
    }
  },

  updateOrgAssignment: async (employeeUuid, orgAssignmentUuid, orgData) => {
    try {
      const response = await axiosInstance.put(
        `/employees/${employeeUuid}/organisation-assignments/${orgAssignmentUuid}`,
        orgData,
      );
      return response.data;
    } catch (error) {
      console.error(
        `Error updating org assignment ${orgAssignmentUuid}:`,
        error,
      );
      throw error;
    }
  },

  // ==========================================
  // 6. SKILL ASSIGNMENTS (Read & Update Only)
  // ==========================================

  getSkillAssignments: async (employeeUuid) => {
    try {
      const response = await axiosInstance.get(
        `/employees/${employeeUuid}/skill-assignments`,
      );
      return response.data.data || [];
    } catch (error) {
      console.error("Error fetching skill assignments:", error);
      return [];
    }
  },

  updateSkillAssignment: async (
    employeeUuid,
    skillAssignmentUuid,
    skillData,
  ) => {
    try {
      const response = await axiosInstance.put(
        `/employees/${employeeUuid}/skill-assignments/${skillAssignmentUuid}`,
        skillData,
      );
      return response.data;
    } catch (error) {
      console.error(
        `Error updating skill assignment ${skillAssignmentUuid}:`,
        error,
      );
      throw error;
    }
  },

  // ==========================================
  // 7. SKILLS CATALOG (Global Read Only)
  // ==========================================

  getSkillsCatalog: async () => {
    try {
      const response = await axiosInstance.get("/skills");
      return response.data.data || [];
    } catch (error) {
      console.error("Error fetching skills catalog:", error);
      return [];
    }
  },
};
